import React, { useState, useEffect } from 'react';
import { getApiUrl, getServerName, getSystemID } from '../config/api';
import ImageModal from './ImageModal';

/** Raw base64 without data: prefix — pick MIME from magic bytes (stored photos are often PNG). */
function dataUrlFromRawBase64(raw) {
  const s = String(raw || '').trim().replace(/\s/g, '');
  if (!s) return null;
  if (s.startsWith('data:')) return s;
  if (s.startsWith('iVBORw0KGgo')) return `data:image/png;base64,${s}`;
  if (s.startsWith('R0lGOD')) return `data:image/gif;base64,${s}`;
  if (s.startsWith('/9j/')) return `data:image/jpeg;base64,${s}`;
  if (s.startsWith('UklGR')) return `data:image/webp;base64,${s}`;
  return `data:image/jpeg;base64,${s}`;
}

const SmartImage = ({ 
  base64Data, 
  photoURL, 
  alt = "Image", 
  className = "",
  style,
  fallbackSrc,
  placeholderType = 'default',
  clickable = true,
  modalTitle
}) => {
  const [loadedImageSrc, setLoadedImageSrc] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loadError, setLoadError] = useState(false);

  // Handle image load errors - fall back to placeholder
  const handleImageError = () => {
    setLoadError(true);
    setLoadedImageSrc(null);
  };

  const handleImageClick = () => {
    if (clickable) {
      setShowModal(true);
    }
  };

  const getImageSrc = () => {
    if (base64Data && base64Data.trim() !== '') {
      return dataUrlFromRawBase64(base64Data);
    }
    if (loadedImageSrc) {
      return loadedImageSrc;
    }
    if (fallbackSrc) {
      return fallbackSrc;
    }
    return null;
  };

  const imageSrc = getImageSrc();

  const hasBase64 = Boolean(base64Data && base64Data.trim() !== '');
  const isDataUrl =
    photoURL && typeof photoURL === 'string' && photoURL.trim().startsWith('data:');

  // Must run on every render (before any return) — hooks cannot follow early returns.
  useEffect(() => {
    if (hasBase64 || isDataUrl || loadError) return;
    if (!photoURL || photoURL.trim() === '' || loadedImageSrc) return;

    if (photoURL === 'null' || photoURL === 'undefined' || photoURL.length < 10) {
      return;
    }

    const loadImageFromURL = async () => {
      try {
        const response = await fetch(getApiUrl('GET_FILE_DATA'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ServerName: getServerName(),
            SystemID: getSystemID(),
            Purpose: 'GETFILEDATA',
            SourceFilePath: photoURL,
            Source: 'RXPROFILEWEB',
            DeviceType: 'RXPROFILEWEB',
          }),
        });

        if (!response.ok) {
          return;
        }

        const data = await response.json();

        if (data && data.DataB64 && typeof data.DataB64 === 'string') {
          const b64 = data.DataB64;
          const dataUrl = dataUrlFromRawBase64(b64) || `data:image/jpeg;base64,${b64}`;
          setLoadedImageSrc(dataUrl);
        } else {
          console.warn('GetFileData response does not contain DataB64 field:', data);
        }
      } catch (err) {
        console.warn('Image load failed (keeping placeholder):', err);
      }
    };

    loadImageFromURL();
  }, [photoURL, loadedImageSrc, hasBase64, isDataUrl, loadError]);

  // If we have base64 data, use it directly (strip newlines — they can break img src)
  if (hasBase64 && !loadError) {
    const src = dataUrlFromRawBase64(base64Data);
    if (src) {
      return (
        <>
          <img 
            src={src}
            alt={alt}
            className={`${className} ${clickable ? 'cursor-pointer hover:opacity-90 transition-opacity' : ''}`}
            style={style}
            onClick={handleImageClick}
            onError={handleImageError}
          />
          {clickable && imageSrc && (
            <ImageModal
              isOpen={showModal}
              onClose={() => setShowModal(false)}
              imageSrc={imageSrc}
              imageAlt={alt}
              title={modalTitle || alt}
            />
          )}
        </>
      );
    }
  }

  // If photoURL is a data URL (base64), use it directly — no fetch needed
  if (isDataUrl && !loadError) {
    const src = photoURL.trim();
    return (
      <>
        <img
          src={src}
          alt={alt}
          className={`${className} ${clickable ? 'cursor-pointer hover:opacity-90 transition-opacity' : ''}`}
          style={style}
          onClick={handleImageClick}
          onError={handleImageError}
        />
        {clickable && (
          <ImageModal
            isOpen={showModal}
            onClose={() => setShowModal(false)}
            imageSrc={src}
            imageAlt={alt}
            title={modalTitle || alt}
          />
        )}
      </>
    );
  }

  // Show loaded image from URL if we got one
  if (loadedImageSrc && !loadError) {
    return (
      <>
        <img 
          src={loadedImageSrc}
          alt={alt}
          className={`${className} ${clickable ? 'cursor-pointer hover:opacity-90 transition-opacity' : ''}`}
          style={style}
          onClick={handleImageClick}
          onError={handleImageError}
        />
        {clickable && imageSrc && (
          <ImageModal
            isOpen={showModal}
            onClose={() => setShowModal(false)}
            imageSrc={imageSrc}
            imageAlt={alt}
            title={modalTitle || alt}
          />
        )}
      </>
    );
  }

  // Show fallback image if provided
  if (fallbackSrc && !loadError) {
    return (
      <>
        <img 
          src={fallbackSrc}
          alt={alt}
          className={`${className} ${clickable ? 'cursor-pointer hover:opacity-90 transition-opacity' : ''}`}
          style={style}
          onClick={handleImageClick}
          onError={handleImageError}
        />
        {clickable && imageSrc && (
          <ImageModal
            isOpen={showModal}
            onClose={() => setShowModal(false)}
            imageSrc={imageSrc}
            imageAlt={alt}
            title={modalTitle || alt}
          />
        )}
      </>
    );
  }

  // Show placeholder - grey box (with subtle indicator if image was removed/expired)
  const isDocument = placeholderType === 'document';
  const isFaceprint = placeholderType === 'faceprint';
  const baseClasses = className
    .split(' ')
    .filter(cls => !cls.startsWith('w-') && !cls.startsWith('h-') && cls !== 'object-cover')
    .join(' ');

  const documentStyles = isDocument ? {
    width: '192px',
    height: '256px',
    minHeight: '256px',
    maxHeight: '256px'
  } : isFaceprint ? {
    width: '64px',  // w-16
    height: '64px', // h-16
    minWidth: '64px',
    minHeight: '64px'
  } : {
    minHeight: '150px'
  };

  // If image failed to load (was deleted/expired), show slightly different styling
  const wasRemoved = loadError && (base64Data || photoURL || fallbackSrc);

  return (
    <div 
      className={`${baseClasses} flex flex-col items-center justify-center ${wasRemoved ? 'bg-gray-50' : 'bg-gray-200'} border ${wasRemoved ? 'border-gray-300 border-dashed' : 'border-gray-300'} rounded-lg`}
      style={documentStyles}
      title={wasRemoved ? 'Image removed per retention policy' : 'No image available'}
    >
      {wasRemoved ? (
        <div className="flex flex-col items-center gap-1 px-2 text-center">
          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-[10px] text-gray-400 leading-tight">
            Removed per<br/>retention policy
          </span>
        </div>
      ) : (
        <svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      )}
    </div>
  );
};

export default SmartImage;